import * as React from 'react';
import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
//@ts-ignore
import ContactIcon from '@css-xp/base/assets/img/icon-avatar.svg';
import { CONTACT_PATH_2 } from '@atlassian/cssxp-typescommon';
import { ColGroup, SearchResultsTable, TableBody, TableHead, SimpleTD, AuditNotes } from '@css-xp/base';
import {
  PageInfo,
  FetchBillingAdminsForTransactionAccountQuery,
  FetchBillingAdminsForTransactionAccountQueryVariables,
} from '@css-xp/graphtypes';
import { fetchBillingAdminsForTransactionAccount } from './queries/fetch-billing-admins-for-transaction-account';

const PAGE_TAB_SEARCH_RESULTS_LOAD_MORE = 15;
export interface BillingContactTab {
  id: string;
  label: string;
  entityName: string;
}

const columns = [
  {
    name: 'Name',
    heading: false,
    customClassNames: 'col-20',
  },
  {
    name: 'Email',
    customClassNames: 'col-30',
  },
  {
    name: '',
  },
];

const BillingContactTab = ({ id, entityName }: BillingContactTab) => {
  const { data, error, loading, fetchMore } = useQuery<
    FetchBillingAdminsForTransactionAccountQuery,
    FetchBillingAdminsForTransactionAccountQueryVariables
  >(fetchBillingAdminsForTransactionAccount, {
    variables: { id: id },
    displayName: `fetch${entityName}BillingContacts`,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  let resultSetSize = 0;
  const billingAdminConnection = data?.transactionAccount?.billingAdmins;
  const pageInfo: PageInfo = billingAdminConnection?.pageInfo || {
    endCursor: null,
    hasNextPage: false,
  };
  const billingAdmins = billingAdminConnection?.edges || [];
  const totalHits = billingAdminConnection?.totalCount || 0;
  const hasNoResults = totalHits === 0;
  const billingAdminsLength = billingAdmins?.length || 0;
  const showLoadMore =
    !!billingAdmins && totalHits > PAGE_TAB_SEARCH_RESULTS_LOAD_MORE && totalHits > billingAdmins?.length;
  resultSetSize += resultSetSize + billingAdminsLength;

  return (
    <div className="billing-contact-tab" data-testid={`${entityName}-billing-contacts`}>
      <SearchResultsTable
        id="billing-contacts"
        isLoading={loading}
        error={error?.toString()}
        noResults={hasNoResults}
        heading={'Billing Admins'}
        entityName="billingAdmins"
        loadMore={{
          showLoadMore,
          totalCount: totalHits,
          resultsCount: resultSetSize,
          onLoadMoreClick: () => {
            return fetchMore({
              variables: {
                searchOptions: {
                  offset: resultSetSize,
                  cursor: pageInfo.endCursor,
                  first: PAGE_TAB_SEARCH_RESULTS_LOAD_MORE,
                },
              },
              //@ts-ignore
              updateQuery: (prev, { fetchMoreResult }) => {
                if (!fetchMoreResult) {
                  return prev;
                }

                const previousData = prev.transactionAccount || data;

                const newData = fetchMoreResult.transactionAccount;
                const newPageInfo = newData?.billingAdmins?.pageInfo;
                const newEdges = newData?.billingAdmins?.edges ?? [];

                if (!previousData.billingAdmins || !newData.billingAdmins) {
                  return prev;
                }

                return {
                  transactionAccount: {
                    // Note: Make sure you return the original id, or nothing will update!
                    id: previousData.id,
                    billingAdmins: {
                      pageInfo: newPageInfo,
                      totalCount: newData.billingAdmins.totalCount,
                      edges: [...billingAdmins, ...newEdges],
                    },
                  },
                };
              },
            });
          },
        }}
      >
        <ColGroup columns={columns} />
        <TableHead columns={columns} />
        <TableBody>
          {!!billingAdmins &&
            billingAdmins.map((billingAdmin, index) => {
              const fullName = billingAdmin?.node.fullName;
              const email = billingAdmin?.node.email;
              return (
                <tr key={index}>
                  <td>
                    {email ? (
                      <Link
                        to={`${CONTACT_PATH_2}/${encodeURIComponent(email)}`}
                        onClick={(e) => e.stopPropagation()}
                        className="avatar-container"
                        data-testid="contact-link"
                      >
                        {/*// @ts-ignore */}
                        <img src={ContactIcon} className="avatar medium" />
                        {fullName || email}
                      </Link>
                    ) : (
                      'Not available'
                    )}
                  </td>
                  <SimpleTD text={email} />
                  <td />
                </tr>
              );
            })}
        </TableBody>
      </SearchResultsTable>
      <AuditNotes id={id} entityName={entityName} />
    </div>
  );
};

export default BillingContactTab;
